// The one rich achievements surface. Header with earned count + "to
// claim" pulse, virtualized rows with medallion, progress bar, Claim
// button and keystone reward pill. Mounted in the hub's right panel and
// in the profile overlay. `onClaimAchievement` null means read-only
// (viewing someone else's profile); progress bars still render because
// the public profile payload carries the counters getAchievementProgress
// reads. `unclaimedAchievements` overrides `profile.unclaimedAchievements`
// (the public profile endpoint omits it, so the self-view passes the
// hub profile's list).
import { ACHIEVEMENTS, ACHIEVEMENT_KEYSTONE_REWARD, getAchievementProgress } from '../utils/arena/achievements';
import { ACCENT, ACCENT_BRIGHT, ACCENT_HI, ACCENT_DEEP, ACCENT_TEXT, PEARL } from '../lib/medievalTheme';
import VirtualList from './VirtualList';
import { KeystoneIcon } from './ui/icons';
import { Tooltip } from './ui/tooltip';
import AchievementOrnamentIcon from './AchievementOrnamentIcon';
import { t } from '../localization/t';

export function AchievementMedallion({ unlocked, pct = 0, size = '2.75cqw', achievementId = null }) {
  // Accept either a raw number (legacy px) or a CSS string (e.g. '2.75cqw').
  // The achievements panel. Like the rest of the hub. Scales with the
  // canvas via cqw, so default to a cqw-based diameter (44px / 16 = 2.75cqw
  // at scale 1.0) and let any explicit caller override with a number.
  const sizeCss = typeof size === 'number' ? `${size}px` : size;
  // The medallion is deliberately NOT built from the accent tokens.
  // Earned badges are a pearly vintage white so they read as the one
  // fixed object on a page whose gold follows the player's background
  // art: the badge is the award, the chrome around it is the frame.
  const stroke = unlocked ? 'rgba(122, 108, 84, 0.6)' : 'rgba(166,160,155,0.35)';
  const fill = unlocked ? 'url(#medal-pearl)' : 'rgba(12,10,8,0.7)';
  const symbolStroke = 'rgba(166,160,155,0.5)';
  const r = 20;
  const circumference = 2 * Math.PI * r;
  const progressOffset = circumference - (circumference * pct) / 100;
  // Glow and drop shadow live on the wrapping <div> as `box-shadow`,
  // NOT a CSS `filter: drop-shadow(...)` on the SVG. CSS filters force
  // every medallion onto its own GPU compositor layer with a Gaussian
  // blur kernel. With 40 medallions on the achievements panel, that's
  // 40 filter layers the rasterizer has to maintain, which is exactly
  // what was making rows "spawn in" 1–2s late on scroll. Box-shadow
  // on a `border-radius: 50%` element produces an identical-looking
  // glow at a fraction of the rasterization cost (and stays cached
  // across scroll).
  const wrapperStyle = {
    width: sizeCss,
    height: sizeCss,
    borderRadius: '50%',
    boxShadow: unlocked ? '0 2px 5px rgba(0,0,0,0.55), 0 0 7px 1px rgba(255, 248, 230, 0.22)' : 'none',
    position: 'relative',
  };
  return (
    <div className="shrink-0" style={wrapperStyle}>
      <svg width={sizeCss} height={sizeCss} viewBox="0 0 44 44" className="block">
        <defs>
          {/* Pearl body: off-centre ivory highlight falling to an aged
              bone edge, with a faint cool lift at the far side so the
              surface reads as nacre rather than flat cream. */}
          <radialGradient id="medal-pearl" cx="38%" cy="32%" r="68%">
            <stop offset="0%" stopColor={PEARL.hi} />
            <stop offset="42%" stopColor={PEARL.body} />
            <stop offset="78%" stopColor={PEARL.shade} />
            <stop offset="100%" stopColor={PEARL.edge} />
          </radialGradient>
          {/* Cool nacre bloom at the lower right, kept very faint. */}
          <radialGradient id="medal-nacre" cx="70%" cy="78%" r="55%">
            <stop offset="0%" stopColor="rgba(198, 206, 228, 0.42)" />
            <stop offset="100%" stopColor="rgba(198, 206, 228, 0)" />
          </radialGradient>
          {/* Rim shading: transparent centre to a warm shadow at the
              edge, which is what gives the disc its convexity. */}
          <radialGradient id="medal-shade" cx="50%" cy="50%" r="50%">
            <stop offset="70%" stopColor="rgba(80, 64, 40, 0)" />
            <stop offset="100%" stopColor="rgba(80, 64, 40, 0.42)" />
          </radialGradient>
          {/* Specular sheen across the upper half. */}
          <linearGradient id="medal-sheen" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255, 255, 255, 0.8)" />
            <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
          </linearGradient>
        </defs>
        <circle cx="22" cy="22" r={r} fill={fill} stroke={stroke} strokeWidth="1.5" />
        {unlocked && (
          <>
            <circle cx="22" cy="22" r={r} fill="url(#medal-nacre)" />
            <circle cx="22" cy="22" r={r} fill="url(#medal-shade)" />
            <ellipse cx="20" cy="13" rx="12" ry="7" fill="url(#medal-sheen)" />
            {/* Bevel: bright inner hairline just inside the rim, dark
                hairline just outside it. */}
            <circle cx="22" cy="22" r={r - 1.6} fill="none" stroke="rgba(255, 255, 255, 0.55)" strokeWidth="0.8" />
            <circle cx="22" cy="22" r={r + 0.9} fill="none" stroke="rgba(0, 0, 0, 0.35)" strokeWidth="0.6" />
          </>
        )}
        {/* Locked-state shield placeholder. Earned achievements show
            their own Viking ornament overlaid below; the shield is
            hidden once the player has the medal so the unique
            silhouette can read clearly against the pearl disc. */}
        {!unlocked && (
          <path
            d="M14 15 L22 11 L30 15 L30 22 Q30 28 22 32 Q14 28 14 22 Z"
            fill="none"
            stroke={symbolStroke}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        )}
        {!unlocked && pct > 0 && (
          <circle
            cx="22"
            cy="22"
            r={r}
            fill="none"
            stroke="rgba(236, 226, 205, 0.9)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            transform="rotate(-90 22 22)"
            style={{ transition: 'stroke-dashoffset 0.4s ease-out' }}
          />
        )}
      </svg>
      {/* Per-achievement Viking ornament. Rendered as a sepia-tinted
          mask sitting just inside the medallion rim once the
          achievement is earned. Same recipe as the celebration
          overlay so the panel row and the unlock moment share a
          glyph. The inset crops the ornament inside the 1.5px rim
          stroke so the silhouette doesn't crash into the rim. */}
      {unlocked && achievementId ? (
        <AchievementOrnamentIcon
          achievementId={achievementId}
          color="rgba(58, 46, 30, 0.86)"
          size={size}
          style={{
            position: 'absolute',
            inset: '12%',
            width: 'auto',
            height: 'auto',
            // Anchor inside the medallion's stacking context so the
            // ornament sits above the SVG disc but below any sibling
            // hover/glow chrome the parent row applies.
            zIndex: 1,
          }}
        />
      ) : null}
    </div>
  );
}

export default function AchievementsPanel({
  profile,
  sorceryCards,
  customDeckCount = 0,
  maxDeckLikes = 0,
  onClaimAchievement = null,
  unclaimedAchievements = null,
  canvasScale = 1,
  header = true,
}) {
  const unclaimedSet = new Set(onClaimAchievement ? (unclaimedAchievements || profile?.unclaimedAchievements || []) : []);
  const unclaimedCount = unclaimedSet.size;
  const earned = profile.achievements || [];
  return (
    <>
      {header ? (
        <>
          {/* Header */}
          <div className="px-5 pt-5 pb-3 shrink-0 flex items-baseline gap-2">
            <span className="arena-heading text-sm font-semibold" style={{ color: `${ACCENT} 0.55)`, textShadow: `0 0 12px ${ACCENT} 0.15)` }}>
              {t('Achievements')}
            </span>
            <span className="text-[0.625cqw] tabular-nums" style={{ color: `${ACCENT} 0.3)` }}>{earned.length}/{ACHIEVEMENTS.length}</span>
            {unclaimedCount > 0 ? (
              <span
                className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.5625cqw] font-bold tabular-nums arena-achievement-unclaimed-pulse"
                style={{
                  background: `${ACCENT} 0.18)`,
                  border: `1px solid ${ACCENT_TEXT}`,
                  color: ACCENT_TEXT,
                  textShadow: `0 0 8px ${ACCENT} 0.5)`,
                }}
                title={unclaimedCount === 1 ? t('1 reward ready to claim') : t('{n} rewards ready to claim', { n: unclaimedCount })}
              >
                {t('{n} to claim', { n: unclaimedCount })}
              </span>
            ) : null}
          </div>
          {/* Divider */}
          <div className="mx-5 h-px" style={{ background: `linear-gradient(90deg, transparent, ${ACCENT} 0.2), transparent)` }} />
        </>
      ) : null}
      {/* Achievement list. Virtualized at 76px fixed height so even
          with several dozen rows only the visible slice is mounted in
          the DOM. The fixed height fits the tallest row variant (with
          progress bar); shorter rows have a few px of breathing room. */}
      <VirtualList
        className="flex-1 px-2 py-3"
        items={ACHIEVEMENTS}
        itemHeight={Math.round(76 * canvasScale)}
        overscan={4}
        keyFor={(a) => a.id}
        renderItem={(a) => {
          const unlocked = earned.includes(a.id);
          const unclaimed = unlocked && unclaimedSet.has(a.id);
          const prog = !unlocked ? getAchievementProgress(a.id, profile, sorceryCards, customDeckCount || 0, maxDeckLikes || 0) : null;
          const hasProgress = prog && prog.target > 1 && prog.current > 0;
          const pct = prog && prog.target > 0 ? Math.min(100, Math.round((prog.current / prog.target) * 100)) : 0;
          // Full-bleed fills, not left-anchored fade-to-transparent: a
          // gradient that dies before the right edge leaves the
          // right-hand corners unpainted and the rounding invisible.
          let rowBackground = 'transparent';
          if (unclaimed) {
            rowBackground = `linear-gradient(100deg, ${ACCENT} 0.2) 0%, ${ACCENT} 0.1) 55%, ${ACCENT} 0.07) 100%)`;
          } else if (unlocked) {
            rowBackground = `linear-gradient(100deg, ${ACCENT} 0.07) 0%, ${ACCENT} 0.02) 100%)`;
          }
          return (
            <Tooltip content={`${a.name}: ${a.description}`} side="bottom" delay={350} maxWidth="24cqw">
              <div
                className={`relative flex items-center gap-3 px-3 py-3 group ${unclaimed ? 'arena-achievement-unclaimed-pulse' : ''}`}
                style={{
                  // Only claimable rows get a drawn container. Everything
                  // else is separated by whitespace alone. Bordering all
                  // 40 entries turns the list into a stack of boxes and
                  // costs the claimable row the contrast it exists for.
                  // Earned rows keep a borderless gold wash: a highlight,
                  // not a plate.
                  height: 'calc(100% - 0.375cqw)',
                  // Radius matched to the panel's 0.5cqw, with the list
                  // inset halved (px-2) to keep the plate close to the
                  // panel wall. The wider the gutter, the less the two
                  // roundings look related.
                  borderRadius: 'var(--radius-lg)',
                  border: unclaimed ? `1px solid ${ACCENT} 0.4)` : '1px solid transparent',
                  background: rowBackground,
                }}
              >
                <AchievementMedallion unlocked={unlocked} pct={pct} achievementId={a.id} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <div className="text-[0.78125cqw] font-semibold tracking-tight truncate" style={{ color: unlocked ? '#e8d5a0' : 'rgba(232,213,160,0.55)', textShadow: unlocked ? '0 1px 2px rgba(0,0,0,0.5)' : 'none' }}>{a.name}</div>
                    {unclaimed ? (
                      <span
                        className="text-[0.5625cqw] font-bold shrink-0"
                        style={{ color: ACCENT_TEXT, textShadow: `0 0 8px ${ACCENT} 0.6)` }}
                      >
                        {t('★ New')}
                      </span>
                    ) : null}
                    {unlocked && !unclaimed ? (
                      <span className="text-[0.5625cqw] font-bold shrink-0" style={{ color: `${ACCENT} 0.7)` }}>{t('Earned')}</span>
                    ) : null}
                  </div>
                  <div className="text-[0.71875cqw] truncate mt-0.5" style={{ color: unlocked ? 'rgba(178,172,166,0.9)' : 'rgba(178,172,166,0.65)' }}>{a.description}</div>
                  {hasProgress ? (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 overflow-hidden rounded-full" style={{ background: 'rgba(0,0,0,0.55)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.6)' }}>
                        <div className="h-full rounded-full transition-[width]" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${ACCENT_DEEP} 1) 0%, ${ACCENT_TEXT} 50%, ${ACCENT_HI} 1) 100%)`, boxShadow: `0 0 6px ${ACCENT_BRIGHT} 0.5)` }} />
                      </div>
                      <span className="text-[0.625cqw] shrink-0 tabular-nums font-semibold" style={{ color: ACCENT_TEXT }}>{prog.current}/{prog.target}</span>
                    </div>
                  ) : null}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  {unclaimed ? (
                    <button
                      type="button"
                      onClick={() => onClaimAchievement?.(a.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 cursor-pointer arena-achievement-claim-btn"
                      style={{
                        background: `linear-gradient(180deg, ${ACCENT} 0.55) 0%, ${ACCENT} 0.3) 100%)`,
                        border: `1.5px solid ${ACCENT_TEXT}`,
                        borderRadius: 'var(--radius-md)',
                        // The fill is translucent gold over a dark panel, so it
                        // composites to a mid-dark olive. Not the bright plate
                        // the 0.55 alpha suggests. Light text is the readable side.
                        color: '#fff3d6',
                        textShadow: '0 1px 2px rgba(0,0,0,0.55)',
                        fontSize: '0.6875cqw',
                        fontWeight: 800,
                        boxShadow: `0 0 16px ${ACCENT} 0.55), inset 0 1px 0 rgba(255,255,255,0.35)`,
                      }}
                      title={t('Claim your keystone reward')}
                    >
                      <KeystoneIcon size={11} />
                      {t('Claim')}
                    </button>
                  ) : null}
                  {!unlocked ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.625cqw] font-bold tabular-nums"
                      style={{
                        background: 'rgba(0,0,0,0.35)',
                        border: `1px solid ${ACCENT} 0.15)`,
                        color: `${ACCENT_BRIGHT} 0.45)`,
                      }}
                    >
                      <KeystoneIcon size={10} />
                      {ACHIEVEMENT_KEYSTONE_REWARD}
                    </span>
                  ) : null}
                </div>
              </div>
            </Tooltip>
          );
        }}
      />
    </>
  );
}
